import {
  Box,
  Divider,
  Skeleton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ClientAccessPanel from "components/accessGroups/ClientAccessPanel";
import {
  switchTree,
  type ClientSwitchState,
} from "components/accessGroups/clientPanelModel";
import type {
  AccessCatalog,
  ClientSwitch,
  ClientSwitchField,
} from "types/interfaces/access";
import StepHeading from "./StepHeading";

interface Props {
  catalog: AccessCatalog | undefined;
  state: ClientSwitchState;
  onSwitch: (field: ClientSwitchField, on: boolean) => void;
  onChoice: (field: "integrations_experience", value: string) => void;
  canEdit: boolean;
}

function SwitchRow({
  item,
  checked,
  disabled,
  onChange,
  nested = false,
}: {
  item: ClientSwitch;
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
  nested?: boolean;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: nested ? 14 : undefined }}>
          {item.name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ lineHeight: 1.4 }}
        >
          {item.description}
        </Typography>
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        inputProps={{ "aria-label": item.name }}
        size={nested ? "small" : "medium"}
      />
    </Box>
  );
}

/** Step 2 of the client user form: the switches that are a client user's whole
 * access, each with what it opens, next to what they add up to. */
export default function ClientUserAccessStep({
  catalog,
  state,
  onSwitch,
  onChoice,
  canEdit,
}: Props) {
  return (
    <Box>
      <StepHeading
        title="User Access"
        subtitle="Turn on the pages this client user opens. Client users have no access group: these switches are their whole access."
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1fr) minmax(0, 1fr)",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {!catalog
            ? Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={72} />
              ))
            : switchTree(catalog).map(({ item, children }) => {
                const on = state[item.field] === true;
                return (
                  <Box
                    key={item.field}
                    sx={{
                      border: "1px solid",
                      borderColor: on ? "primary.main" : "divider",
                      borderRadius: 1,
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    <SwitchRow
                      item={item}
                      checked={on}
                      disabled={!canEdit}
                      onChange={(next) => onSwitch(item.field, next)}
                    />
                    {item.choice_field && (
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        fullWidth
                        value={
                          state[item.choice_field] ??
                          item.choices[0]?.value ??
                          null
                        }
                        onChange={(_, next: string | null) => {
                          if (next && item.choice_field) {
                            onChoice(item.choice_field, next);
                          }
                        }}
                        disabled={!canEdit || !on}
                        aria-label={`${item.name} experience`}
                      >
                        {item.choices.map((choice) => (
                          <ToggleButton
                            key={choice.value}
                            value={choice.value}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            {choice.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    )}
                    {children.map((child) => (
                      <Box key={child.field}>
                        <Divider sx={{ mb: 1.5 }} />
                        <SwitchRow
                          item={child}
                          nested
                          checked={state[child.field] === true}
                          disabled={!canEdit || !on}
                          onChange={(next) => onSwitch(child.field, next)}
                        />
                      </Box>
                    ))}
                  </Box>
                );
              })}
        </Box>
        {catalog ? (
          <ClientAccessPanel
            catalog={catalog}
            state={state}
            subHeader="What this client user sees and does with these switches."
          />
        ) : (
          <Skeleton variant="rounded" height={220} />
        )}
      </Box>
    </Box>
  );
}
